import { convertToQueryParams } from 'helpers/queryHelpers';
import { rawId } from 'utils/rawId';
import { parseLoomId } from 'utils/urlParser';

import { useState } from 'react';

import { Attachment } from 'schema/Post/types';

// Video Provider Constants
export const VideoProviderType = {
  VIDEO_PROXY: 'video_proxy',
  S3: 's3',
  UNKNOWN: '',
} as const;

export type VideoProviderType = (typeof VideoProviderType)[keyof typeof VideoProviderType];

// Constants
const DEFAULT_PLAYER_PARAMS = { autoplay: true };

export interface VideoPlayerState {
  isPlaying: boolean;
  isLoading: boolean;
  videoUrl: string;
  loomId: string | null;
  thumbnailUrl: string;
  playerParams: string;
  provider: VideoProviderType | null;
  isS3: boolean;
}

export interface VideoPlayerActions {
  handleThumbnailClick: () => Promise<void>;
  setIsPlaying: (playing: boolean) => void;
}

export const useVideoPlayer = (attachment: Attachment) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Early return if no valid attachment ID
  const attachmentIdRaw = attachment.id ? rawId(attachment.id) : null;
  if (!attachmentIdRaw) {
    return {
      state: {
        isPlaying: false,
        isLoading: false,
        videoUrl: '',
        loomId: null,
        thumbnailUrl: '',
        playerParams: '',
        provider: null,
        isS3: false,
      },
      actions: {
        handleThumbnailClick: async () => {},
        setIsPlaying: () => {},
      },
    };
  }

  // Extract video data
  const videoUrl = attachment.url || '';
  const loomId = parseLoomId(videoUrl);

  // Map string provider values to enum values
  const getProvider = (providerString?: string): VideoProviderType | null => {
    switch (providerString) {
      case 'video_proxy':
        return VideoProviderType.VIDEO_PROXY;
      case 's3':
        return VideoProviderType.S3;
      default:
        return null;
    }
  };

  const provider = getProvider(attachment?.provider || '');
  // A full list of player params can be found here: https://docs.wistia.com/docs/embed-options-and-plugins#list-of-all-options
  const playerParams = convertToQueryParams(DEFAULT_PLAYER_PARAMS);

  const handleThumbnailClick = async () => {
    setIsLoading(true);
    try {
      setIsPlaying(true);
    } finally {
      setIsLoading(false);
    }
  };

  const state: VideoPlayerState = {
    isPlaying,
    isLoading,
    videoUrl,
    loomId,
    thumbnailUrl: attachment?.thumbnailUrl || '',
    provider,
    isS3: provider === VideoProviderType.S3,
    playerParams,
  };

  const actions: VideoPlayerActions = {
    handleThumbnailClick,
    setIsPlaying,
  };

  return { state, actions };
};
